// FNP-1960: product DRAFTS. This file mirrors the deployed marketplace API
// contract. The GraphQL below is the source; every type under it follows it.
//
//   enum ProductStatus { DRAFT }
//   enum PricingSource { NATIVE LINKED }
//   enum DimensionKind { FLAT CONTRACT USAGE }
//   enum UsageSourceKind { BILLING }
//   enum PriceTerm { MONTHLY ANNUAL TWO_YEARS THREE_YEARS }
//   type UsageSource { source: UsageSourceKind!  meterId: ID! }
//   type BillingLink { productId: ID!  catalogVersion: String! }
//   type Price { term: PriceTerm  amount: String!  currency: String! }      # term null only on USAGE
//   type PricingDimension { dimensionId: ID!  name: String!  description: String  unit: String!
//                           kind: DimensionKind!  usageSource: UsageSource  prices: [Price!]! }
//   type Product { id: ID!  name: String!  summary: String!  description: String!  eulaUrl: String!
//                  supportContact: String!  status: ProductStatus!  pricingSource: PricingSource!
//                  billingLink: BillingLink  dimensions: [PricingDimension!]!  version: Int!
//                  created: AWSDateTime!  updated: AWSDateTime! }
//   type ProductConnection { items: [Product!]!  nextToken: String }
//   input UsageSourceInput { source: UsageSourceKind!  meterId: ID! }
//   input PriceInput { term: PriceTerm  amount: String!  currency: String! }
//   input PricingDimensionInput { dimensionId: ID  name: String!  description: String  unit: String!
//                                 kind: DimensionKind!  usageSource: UsageSourceInput  prices: [PriceInput!]! }
//   input CreateProductInput { name: String!  summary: String!  description: String!  eulaUrl: String!
//                              supportContact: String!  dimensions: [PricingDimensionInput!]! }
//   input UpdateProductInput { id: ID!  expectedVersion: Int!  name: String!  summary: String!
//                              description: String!  eulaUrl: String!  supportContact: String!
//                              dimensions: [PricingDimensionInput!]! }
//   type Query { getProduct(id: ID!): Product   listProducts(limit: Int, nextToken: String): ProductConnection }
//   type Mutation { createProduct(input: CreateProductInput!): Product   updateProduct(input: UpdateProductInput!): Product }
//
// There is NO tenantId in the client, NO delete and NO filter argument.

export type ProductStatus = 'DRAFT';
export type PricingSource = 'NATIVE' | 'LINKED';
export type DimensionKind = 'FLAT' | 'CONTRACT' | 'USAGE';
export type UsageSourceKind = 'BILLING';
export type PriceTerm = 'MONTHLY' | 'ANNUAL' | 'TWO_YEARS' | 'THREE_YEARS';

export const DIMENSION_KINDS: readonly DimensionKind[] = ['FLAT', 'CONTRACT', 'USAGE'];
export const PRICE_TERMS: readonly PriceTerm[] = ['MONTHLY', 'ANNUAL', 'TWO_YEARS', 'THREE_YEARS'];
export const CURRENCY = 'USD';
export const MAX_DIMENSIONS = 30;

export interface UsageSource {
    source: UsageSourceKind;
    meterId: string;
}

export interface BillingLink {
    productId: string;
    catalogVersion: string;
}

export interface Price {
    term?: PriceTerm | null;
    amount: string;
    currency: string;
}

export interface PricingDimension {
    dimensionId: string;
    name: string;
    description?: string | null;
    unit: string;
    kind: DimensionKind;
    usageSource?: UsageSource | null;
    prices: Price[];
}

export interface Product {
    id: string;
    name: string;
    summary: string;
    description: string;
    eulaUrl: string;
    supportContact: string;
    status: ProductStatus;
    pricingSource: PricingSource;
    billingLink?: BillingLink | null;
    dimensions: PricingDimension[];
    version: number;
    created: string;
    updated: string;
}

export type ProductConnection = {
    items: Product[];
    nextToken?: string | null;
};

export type ListProducts = { listProducts?: ProductConnection | null };
export type GetProduct = { getProduct?: Product | null };
export type CreateProduct = { createProduct?: Product | null };
export type UpdateProduct = { updateProduct?: Product | null };

// ---- API inputs (what the mutations accept, nothing else) ----

export interface UsageSourceInput {
    source: UsageSourceKind;
    meterId: string;
}

export interface PriceInput {
    term?: PriceTerm;
    amount: string;
    currency: string;
}

export interface PricingDimensionInput {
    dimensionId?: string;
    name: string;
    description?: string;
    unit: string;
    kind: DimensionKind;
    usageSource?: UsageSourceInput;
    prices: PriceInput[];
}

export interface CreateProductInput {
    name: string;
    summary: string;
    description: string;
    eulaUrl: string;
    supportContact: string;
    dimensions: PricingDimensionInput[];
}

export interface UpdateProductInput extends CreateProductInput {
    id: string;
    expectedVersion: number;
}

// ---- Form values (what Formik holds; strings only, so inputs stay controlled) ----

export interface PriceFormValues {
    term: PriceTerm | '';
    amount: string;
    currency: string;
}

export interface DimensionFormValues {
    // Set only on a unit that already exists; an update sends it back so the
    // server keeps the unit's identity. A new unit has none.
    dimensionId?: string;
    name: string;
    description: string;
    unit: string;
    kind: DimensionKind | '';
    usageSource?: UsageSource | null;
    prices: PriceFormValues[];
}

export interface ProductFormValues {
    name: string;
    summary: string;
    description: string;
    eulaUrl: string;
    supportContact: string;
    dimensions: DimensionFormValues[];
}
